import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_COURSES,
  DREAMS,
  DREAM_KEYS,
  DREAM_REWARD_WP,
  MISSION_REWARD_WP,
  PERKS,
  PERK_KEYS,
  REP_LEVELS,
  SKILLS,
  repLevel,
  repTitle,
  type DreamKey,
  type GoalsView,
  type MissionKey,
  type PerkKey,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Button } from '@london-life/ui/components/button';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useCatalogue } from '../lib/content';

const longDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });

// Goals: daily missions and the streak, wishes, the lifetime dream, certificates and perks. Rewards are wish points.
export function GoalsTab({ onMessage }: { onMessage: (message: string) => void }) {
  const courses = useCatalogue()?.courses ?? DEFAULT_COURSES;
  const [goals, setGoals] = useState<GoalsView | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [busy, setBusy] = useState(false);
  const [choosingDream, setChoosingDream] = useState(false);
  const load = useCallback(() => {
    setFailure(null);
    api.goals
      .get()
      .then((reply) => setGoals(reply.goals))
      .catch((error: unknown) => setFailure(describeError(error)));
  }, []);
  useEffect(load, [load]);
  const run = async (action: () => ReturnType<typeof api.goals.get>) => {
    setBusy(true);
    setFailure(null);
    try {
      const reply = await action();
      setGoals(reply.goals);
      if (reply.message) onMessage(reply.message);
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  if (!goals)
    return failure ? <ErrorNotice error={failure} onRetry={load} /> : <p className="profile-hint">Loading…</p>;

  const level = repLevel(goals.earnedWishPoints);
  const next = REP_LEVELS[level];
  const dream = goals.dream && goals.dream.key in DREAMS ? (goals.dream.key as DreamKey) : null;
  return (
    <div className="profile-grid">
      {failure && <ErrorNotice error={failure} />}
      <section className="profile-card" aria-labelledby="goals-rep">
        <h3 id="goals-rep">
          {repTitle(goals.earnedWishPoints)} <span className="skills-level">Rep level {level}</span>
        </h3>
        <p className="goals-points">
          <strong>{goals.wishPoints}</strong> wish points
        </p>
        <span
          className="needs-bar"
          role="meter"
          aria-label="Reputation"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={
            next
              ? Math.round(((goals.earnedWishPoints - REP_LEVELS[level - 1]) / (next - REP_LEVELS[level - 1])) * 100)
              : 100
          }
        >
          <b
            style={{
              width: `${next ? ((goals.earnedWishPoints - REP_LEVELS[level - 1]) / (next - REP_LEVELS[level - 1])) * 100 : 100}%`,
              background: 'var(--ll-purple)',
            }}
          />
        </span>
        <p className="profile-hint">
          Earn wish points from missions, wishes and your dream. Every point you earn raises your reputation; spend them
          on perks.
        </p>
      </section>

      <section className="profile-card" aria-labelledby="goals-missions">
        <h3 id="goals-missions">
          Today’s missions <span className="profile-points good">🔥 {goals.streak}-day streak</span>
        </h3>
        <ul className="goals-list">
          {goals.missions.map((mission) => (
            <li key={mission.key}>
              <em aria-hidden="true">{mission.icon}</em>
              <span>
                <strong>{mission.label}</strong>
                <small>
                  {mission.done} of {mission.goal} · +{MISSION_REWARD_WP} WP
                </small>
              </span>
              {mission.claimed ? (
                <span className="profile-badge">Done</span>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  disabled={busy || mission.done < mission.goal}
                  onClick={() => void run(() => api.goals.claimMission(mission.key as MissionKey))}
                >
                  Claim
                </Button>
              )}
            </li>
          ))}
        </ul>
        <p className="profile-hint">
          Finish all three for a bonus that grows with your streak. New missions at midnight.
        </p>
      </section>

      <section className="profile-card" aria-labelledby="goals-wishes">
        <h3 id="goals-wishes">Wishes</h3>
        <ul className="goals-list">
          {goals.wishes.map((wish) => (
            <li key={wish.activityId}>
              <em aria-hidden="true">{wish.granted ? '✨' : '💭'}</em>
              <span>
                <strong>{wish.label}</strong>
                <small>{wish.granted ? 'Granted' : '+10 WP when you do it today'}</small>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="profile-card" aria-labelledby="goals-dream">
        <h3 id="goals-dream">Lifetime dream</h3>
        {dream && goals.dream && !choosingDream ? (
          <>
            <p className="career-main">
              <strong>
                {DREAMS[dream].icon} {DREAMS[dream].label}
              </strong>
              <span>{Math.round(goals.dream.progress * 100)}% of the way</span>
            </p>
            <span
              className="needs-bar"
              role="meter"
              aria-label="Dream"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(goals.dream.progress * 100)}
            >
              <b style={{ width: `${goals.dream.progress * 100}%`, background: 'var(--ll-gold)' }} />
            </span>
            {goals.dream.achieved && !goals.dream.claimed && (
              <Button type="button" disabled={busy} onClick={() => void run(() => api.goals.claimDream())}>
                Claim {DREAM_REWARD_WP} wish points
              </Button>
            )}
            {goals.dream.claimed && <span className="profile-badge">Dream come true</span>}
            <Button
              type="button"
              variant="outline"
              disabled={Boolean(goals.dream.changeFrom)}
              onClick={() => setChoosingDream(true)}
            >
              Change dream
            </Button>
            <p className="profile-hint">
              {goals.dream.changeFrom
                ? `You can change your dream again from ${longDate(goals.dream.changeFrom)}.`
                : 'You can change your dream once a week.'}
            </p>
          </>
        ) : (
          <div className="traits-picker">
            {DREAM_KEYS.map((key) => (
              <button
                type="button"
                key={key}
                aria-pressed={dream === key}
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    const reply = await api.goals.chooseDream(key);
                    setChoosingDream(false);
                    return reply;
                  })
                }
              >
                <em aria-hidden="true">{DREAMS[key].icon}</em>
                <strong>{DREAMS[key].label}</strong>
                <small>+{DREAM_REWARD_WP} WP when it comes true</small>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="profile-card" aria-labelledby="goals-courses">
        <h3 id="goals-courses">Certificates</h3>
        <p className="profile-hint">
          Enrol, attend classes and pass each lesson&apos;s quiz in 🎓 Courses on your phone. A certificate makes its
          skill grow faster, and its job promotes you sooner.
        </p>
        {goals.certificates.length ? (
          <ul className="goals-list">
            {goals.certificates.map((id) => {
              const course = courses.courses.find((item) => item.id === id);
              return (
                <li key={id}>
                  <em aria-hidden="true">📜</em>
                  <span>
                    <strong>{course?.label ?? id}</strong>
                    <small>Certificate earned · {course ? SKILLS[course.skill].label : ''}</small>
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="profile-hint">No certificates yet.</p>
        )}
      </section>

      <section className="profile-card" aria-labelledby="goals-perks">
        <h3 id="goals-perks">Perks</h3>
        <ul className="goals-list">
          {PERK_KEYS.map((perk) => {
            const owned = goals.perks.includes(perk);
            return (
              <li key={perk}>
                <em aria-hidden="true">{PERKS[perk].icon}</em>
                <span>
                  <strong>{PERKS[perk].label}</strong>
                  <small>{PERKS[perk].description}</small>
                </span>
                {owned ? (
                  <span className="profile-badge">Owned</span>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy || goals.wishPoints < PERKS[perk].costWp}
                    onClick={() => void run(() => api.goals.buyPerk(perk as PerkKey))}
                  >
                    {PERKS[perk].costWp} WP
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
